import { useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { AdminChurch } from '../../shared/api/types';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe, useSession } from '../session/SessionProvider';

const RELATION_LABELS: Record<string, string> = {
  member: 'Miembro',
  guest_preacher: 'Predicador invitado',
  teacher: 'Maestro',
  speaker: 'Conferencista',
  musician: 'Músico',
  worship_team: 'Alabanza',
  collaborator: 'Colaborador',
  volunteer: 'Voluntario',
  'role:admin': 'Administrador',
  'role:department_leader': 'Líder',
  'role:member': 'Miembro',
  'role:guest': 'Invitado',
};

/** Etiqueta breve de cómo se relaciona la Persona con la Iglesia. */
export function relationLabel(relations: string[]): string {
  const labels = [...new Set(relations.map((r) => RELATION_LABELS[r]).filter(Boolean))];
  return labels.join(' · ');
}

/**
 * Selector del contexto de actuación: en qué Iglesia estoy actuando ahora.
 * Contexto ≠ Membresía: incluye Iglesias donde la Persona es invitada o tiene un rol.
 */
export function ContextSwitcher() {
  const me = useMe();
  const { switchContext } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (me.contexts.length === 0 && !me.isPlatformAdmin) return null;
  const support = inSupportMode(me);
  // Con una sola iglesia no hay nada que elegir: solo su nombre.
  const only = me.contexts.length === 1 && !support && !me.isPlatformAdmin ? me.contexts[0] : undefined;
  if (only && only.churchId === me.activeChurchId) {
    return (
      <div className="context-switcher context-switcher--single">
        <span className="context-switcher__name">{only.churchName || 'Iglesia'}</span>
      </div>
    );
  }

  async function onChange(event: ChangeEvent<HTMLSelectElement>) {
    setBusy(true);
    setError(null);
    try {
      await switchContext(event.target.value || null);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="context-switcher">
      <label htmlFor="context-select" className="visually-hidden">
        Iglesia en la que actúas
      </label>
      <select id="context-select" value={me.activeChurchId ?? ''} onChange={onChange} disabled={busy}>
        <option value="" disabled>
          Elige una iglesia…
        </option>
        {support ? <option value={me.activeChurchId!}>Iglesia en modo soporte (Superadmin)</option> : null}
        {me.contexts.map((c) => (
          <option key={c.churchId} value={c.churchId}>
            {c.churchName || 'Iglesia'} — {relationLabel(c.relations)}
          </option>
        ))}
      </select>
      {error ? (
        <span className="context-switcher__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

/** La administración de KEORA actúa en una iglesia de la que no forma parte (entró desde el Panel KEORA). */
export function inSupportMode(me: { isPlatformAdmin: boolean; activeChurchId: string | null; contexts: { churchId: string }[] }): boolean {
  return me.isPlatformAdmin && !!me.activeChurchId && !me.contexts.some((c) => c.churchId === me.activeChurchId);
}

/** Franja fija en todas las pantallas mientras dura el modo soporte; «Salir» regresa al Panel KEORA. */
export function SupportBanner() {
  const me = useMe();
  const { switchContext } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const support = inSupportMode(me);
  const churches = useAsync(
    (signal) => (support ? api.get<{ items: AdminChurch[] }>('/multichurch/admin/churches', signal).then((r) => r.items).catch(() => []) : Promise.resolve([])),
    [support, me.activeChurchId],
  );
  if (!support) return null;
  const name = churches.status === 'ready' ? churches.data.find((c) => c.id === me.activeChurchId)?.name : undefined;

  async function leave() {
    setBusy(true);
    setError(null);
    try {
      // Sin iglesia activa se sale del modo soporte.
      await switchContext(null);
      navigate('/app/admin/iglesias');
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <div className="support-banner" role="status">
      <span>
        Estás en modo soporte en <strong>{name ?? 'esta iglesia'}</strong>
      </span>
      <span aria-hidden="true">·</span>
      <button type="button" className="support-banner__exit" onClick={() => void leave()} disabled={busy}>
        Salir
      </button>
      {error ? <span role="alert">{error}</span> : null}
    </div>
  );
}
